import { PolarArea } from 'react-chartjs-2';
import { registerCharts } from './chartSetup';
import { useChartTheme } from './useChartTheme';

registerCharts();

const PALETTE = [
  'rgba(43,90,158,0.55)',
  'rgba(200,85,58,0.55)',
  'rgba(46,125,50,0.55)',
  'rgba(217,119,6,0.55)',
  'rgba(120,113,108,0.55)',
  'rgba(157,61,37,0.55)',
  'rgba(29,61,110,0.55)',
  'rgba(142,135,124,0.55)',
];

interface Props {
  scores: Record<string, number>;
}

// Apollo stamps `__typename` onto every object; keep only real numeric scores.
const numericEntries = (scores: Record<string, number>) =>
  Object.entries(scores).filter(
    ([k, v]) =>
      k !== '__typename' && typeof v === 'number' && Number.isFinite(v),
  );

// Convey trace emotions: even a 0.2% score is shown in the legend, where a
// near-zero polar wedge would be invisible.
const fmtPct = (v: number) => {
  const p = v * 100;
  if (p <= 0) return '0%';
  if (p < 0.1) return '<0.1%';
  return `${p.toFixed(1)}%`;
};

export default function EmotionPolar({ scores }: Props) {
  const theme = useChartTheme();
  const entries = numericEntries(scores);

  const data = {
    labels: entries.map(
      ([k, v]) => `${k[0].toUpperCase() + k.slice(1)} · ${fmtPct(v)}`,
    ),
    datasets: [
      {
        data: entries.map(([, v]) => Math.round(v * 1000) / 10),
        backgroundColor: entries.map((_, i) => PALETTE[i % PALETTE.length]),
        borderWidth: 0,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'right' as const,
        labels: { color: theme.text, font: { size: 11 } },
      },
    },
    scales: {
      r: {
        grid: { color: theme.grid },
        angleLines: { color: theme.grid },
        ticks: { display: false, backdropColor: 'transparent' },
      },
    },
  };

  // Inner content only; SectionShell supplies the `emotions` anchor + eyebrow.
  return (
    <div className="POST__ANALYSIS__chartcard">
      <PolarArea data={data} options={options} />
    </div>
  );
}
